import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Input,
  Button,
  HStack,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  UnorderedList,
  ListItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Select,
  Icon
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import USNBillerComplianceTab from './USNBillerComplianceTab';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const USNCodingQualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  
  const [data, setData] = useState([]);
  const [emptyCasesData, setEmptyCasesData] = useState({ cases: [], daily_trend: [], total_empty: 0 });
  const [volumeData, setVolumeData] = useState([]);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Default to specific date range (37 days ago to 28 days ago) and organization
  const startDateCalc = new Date();
  startDateCalc.setDate(startDateCalc.getDate() - 37);
  const defaultStartDate = startDateCalc.toISOString().split('T')[0];
  
  const endDateCalc = new Date();
  endDateCalc.setDate(endDateCalc.getDate() - 28);
  const defaultEndDate = endDateCalc.toISOString().split('T')[0];
  
  const defaultOrg = '';
  
  const [filters, setFilters] = useState({
    start_date: defaultStartDate,
    end_date: defaultEndDate,
    technologist: '',
    surgeon: '',
    technical_report_org: defaultOrg
  });

  const fetchData = async (overrides = {}) => {
    if (!accessToken) return;
    
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      const p_start = overrides.start_date !== undefined ? overrides.start_date : filters.start_date;
      const p_end = overrides.end_date !== undefined ? overrides.end_date : filters.end_date;
      const p_tech = overrides.technologist !== undefined ? overrides.technologist : filters.technologist;
      const p_surg = overrides.surgeon !== undefined ? overrides.surgeon : filters.surgeon;
      const p_org = overrides.technical_report_org !== undefined ? overrides.technical_report_org : filters.technical_report_org;

      if (p_start) queryParams.append('start_date', p_start);
      if (p_end) queryParams.append('end_date', p_end);
      if (p_tech) queryParams.append('technologist', p_tech);
      if (p_surg) queryParams.append('surgeon', p_surg);
      if (p_org) queryParams.append('technical_report_org', p_org);

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/usn/coding-quality?${queryString}` : '/usn/coding-quality';

      // Also fetch empty cases (hardcoded to 30 days on backend, only pass org_id)
      const emptyCasesParams = new URLSearchParams();
      if (p_org) emptyCasesParams.append('org_id', p_org);
      const emptyCasesEndpoint = `/usn/empty-cases?${emptyCasesParams.toString()}`;
      
      const volumeEndpoint = `/usn/volume-analysis`;

      const [response, emptyCasesResponse, volumeResponse] = await Promise.all([
        callApi(endpoint, accessToken, { method: 'GET' }),
        callApi(emptyCasesEndpoint, accessToken, { method: 'GET' }),
        callApi(volumeEndpoint, accessToken, { method: 'GET' })
      ]);
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setData(result.data);
          if (result.available_orgs) {
            setAvailableOrgs(result.available_orgs);
          }
        } else {
          setError(result.message || 'Failed to fetch data');
        }
      } else {
        setError(`API Error: ${response.status}`);
      }

      if (emptyCasesResponse.ok) {
        const emptyResult = await emptyCasesResponse.json();
        if (!emptyResult.error) {
          setEmptyCasesData(emptyResult);
        }
      }
      
      if (volumeResponse.ok) {
        const volumeResult = await volumeResponse.json();
        if (volumeResult.status === 'success') {
          setVolumeData(volumeResult.data);
        }
      }

    } catch (err) {
      console.error("Error fetching coding quality data:", err);
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filters.start_date, filters.end_date]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      technologist: '',
      surgeon: '',
      technical_report_org: defaultOrg
    });
    fetchData({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      technologist: '',
      surgeon: '',
      technical_report_org: defaultOrg
    });
  };

  const handleTechnologistClick = (techName) => {
    const newTech = techName === 'Unknown' ? '' : techName;
    setFilters(prev => ({ ...prev, technologist: newTech }));
    setTabIndex(0); // Switch back to Overview tab
    fetchData({ technologist: newTech });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ChevronUpIcon ml={1} /> : <ChevronDownIcon ml={1} />;
  };

  const SortableHeader = ({ label, columnKey, isNumeric = false }) => (
    <Th 
      cursor="pointer" 
      onClick={() => handleSort(columnKey)}
      _hover={{ bg: 'gray.100' }}
      isNumeric={isNumeric}
    >
      <Flex align="center" justify={isNumeric ? "flex-end" : "flex-start"}>
        {label}
        <SortIcon columnKey={columnKey} />
      </Flex>
    </Th>
  );

  // --- Data Processing for Volume Analysis ---
  const processedVolumeData = useMemo(() => {
    if (!volumeData || volumeData.length === 0) return { processedTrend: [], unprocessedTrend: [], orgs: [] };

    const start = new Date(filters.start_date);
    const end = new Date(filters.end_date);
    
    // Generate zero-filled date array
    const dateArray = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      dateArray.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const processedMap = {};
    const unprocessedMap = {};
    const orgSet = new Set();

    // Initialize maps with zero-filled dates
    dateArray.forEach(date => {
      processedMap[date] = { date, Total: 0 };
      unprocessedMap[date] = { date, Unprocessed: 0 };
    });

    volumeData.forEach(item => {
      if (item.is_processed && item.date_of_surgery) {
        // Convert MM/DD/YYYY to YYYY-MM-DD
        let dateStr = item.date_of_surgery;
        const parts = item.date_of_surgery.split('/');
        if (parts.length === 3) {
          dateStr = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
        
        // Only count if within filter range
        if (processedMap[dateStr]) {
          const org = item.technical_report_org || 'Unknown';
          orgSet.add(org);
          
          processedMap[dateStr].Total++;
          processedMap[dateStr][org] = (processedMap[dateStr][org] || 0) + 1;
        }
      } else if (!item.is_processed && item.date_created) {
        const dateStr = item.date_created.split('T')[0];
        if (unprocessedMap[dateStr]) {
          unprocessedMap[dateStr].Unprocessed++;
        }
      }
    });

    // Ensure all orgs exist on all dates for Recharts
    const orgs = Array.from(orgSet);
    const processedTrend = Object.values(processedMap).map(day => {
      orgs.forEach(org => {
        if (day[org] === undefined) day[org] = 0;
      });
      return day;
    }).sort((a, b) => a.date.localeCompare(b.date));

    const unprocessedTrend = Object.values(unprocessedMap).sort((a, b) => a.date.localeCompare(b.date));

    return { processedTrend, unprocessedTrend, orgs };
  }, [volumeData, filters.start_date, filters.end_date]);

  // --- Data Processing for Charts and KPIs ---
  const processedData = useMemo(() => {
    const totalCases = data.length;
    let needsReviewCount = 0;
    let preliminaryCount = 0;
    
    const dailyMap = {};
    const issuesMap = {};
    const techSummaryMap = {};

    data.forEach(item => {
      if (item.needs_review) needsReviewCount++;
      if (item.has_preliminary) preliminaryCount++;

      // Daily Trend (using date_of_surgery, fallback to created_at)
      let dateStr = 'Unknown';
      if (item.date_of_surgery) {
        // Convert MM/DD/YYYY to YYYY-MM-DD for sorting
        const parts = item.date_of_surgery.split('/');
        if (parts.length === 3) {
          dateStr = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        } else {
          dateStr = item.date_of_surgery;
        }
      } else if (item.created_at) {
        dateStr = item.created_at.split('T')[0];
      }

      if (!dailyMap[dateStr]) {
        dailyMap[dateStr] = { date: dateStr, Clean: 0, 'Preliminary': 0, total: 0 };
      }
      
      dailyMap[dateStr].total++;

      if (item.has_preliminary) {
        dailyMap[dateStr]['Preliminary']++;
      } else {
        dailyMap[dateStr]['Clean']++;
      }

      // Top Issues
      if (item.cpt_issues && item.cpt_issues.length > 0) {
        item.cpt_issues.forEach(issue => {
          const key = `${issue.cpt_code} - ${issue.issue_category}`;
          issuesMap[key] = (issuesMap[key] || 0) + 1;
        });
      }

      // Technologist Summary
      const tech = item.technologist_name || 'Unknown';
      if (!techSummaryMap[tech]) {
        techSummaryMap[tech] = { name: tech, total: 0, needsReview: 0, preliminary: 0 };
      }
      techSummaryMap[tech].total++;
      if (item.needs_review) techSummaryMap[tech].needsReview++;
      if (item.has_preliminary) techSummaryMap[tech].preliminary++;
    });

    const dailyTrend = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
    
    const topIssues = Object.entries(issuesMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10

    const allCases = data;

    const techSummary = Object.values(techSummaryMap).map(tech => {
      const passedCodes = tech.total - tech.preliminary;
      return {
        ...tech,
        passedCodes,
        passedCodePct: tech.total > 0 ? ((passedCodes / tech.total) * 100).toFixed(1) : 0
      };
    }).sort((a, b) => b.total - a.total); // Sort by volume descending

    const passedCodesTotal = totalCases - preliminaryCount;

    // Sort allCases based on sortConfig
    const sortedCases = [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases for sorting
      if (sortConfig.key === 'status') {
        aValue = a.needs_review ? 2 : (a.has_preliminary ? 1 : 0);
        bValue = b.needs_review ? 2 : (b.has_preliminary ? 1 : 0);
      } else if (sortConfig.key === 'provider_info') {
        aValue = a.technologist_name || '';
        bValue = b.technologist_name || '';
      } else if (sortConfig.key === 'payer_carrier') {
        aValue = a.payer_type || '';
        bValue = b.payer_type || '';
      }

      // Handle nulls/undefined
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return {
      kpis: {
        total: totalCases,
        passedCodePct: totalCases ? ((passedCodesTotal / totalCases) * 100).toFixed(1) : 0
      },
      dailyTrend,
      topIssues,
      allCases: sortedCases,
      techSummary
    };
  }, [data, sortConfig]);

  const exportToCSV = () => {
    if (!processedData.allCases || processedData.allCases.length === 0) return;

    const headers = [
      'Create Date',
      'DOS',
      'Case ID',
      'Case Link',
      'Technologist',
      'Surgeon',
      'Procedure Name',
      'Payer',
      'Carrier',
      'Org',
      'Pain Case',
      'Approval Score',
      'Needs Review Score',
      'V2 Approval Score',
      'Dx Review Required Score',
      'Status',
      'Details'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '""';
      const stringified = String(str);
      if (stringified.includes(',') || stringified.includes('"') || stringified.includes('\n')) {
        return `"${stringified.replace(/"/g, '""')}"`;
      }
      return `"${stringified}"`;
    };

    const csvRows = processedData.allCases.map(item => {
      const createDate = item.created_at ? item.created_at.split('T')[0] : 'N/A';
      const dos = item.date_of_surgery || 'N/A';
      const caseId = item.external_case_id;
      const caseLink = `https://app.revedy.io/ionm-strict-analysis/${item.external_case_id}`;
      const tech = item.technologist_name || 'N/A';
      const surg = item.referring_provider || 'N/A';
      const proc = item.procedure_name || 'N/A';
      const payer = item.payer_type || 'N/A';
      const carrier = item.carrier_name || 'N/A';
      const org = item.technical_report_org || 'N/A';
      const painCase = item.is_pain_management ? 'Yes' : 'No';
      const needsReviewScore = item.needs_review_score !== null && item.needs_review_score !== undefined ? item.needs_review_score : 'N/A';
      const dxReviewRequiredScore = item.dx_review_required_score !== null && item.dx_review_required_score !== undefined ? item.dx_review_required_score : 'N/A';
      const approvalScore = item.approval_score !== null && item.approval_score !== undefined ? item.approval_score : 'N/A';
      const approvalScoreV2 = item.approval_score_v2 !== null && item.approval_score_v2 !== undefined ? item.approval_score_v2 : 'N/A';
      
      const flagType = [];
      if (item.needs_review) flagType.push('Claim Review');
      if (item.has_preliminary) flagType.push('Row Preliminary');
      if (!item.needs_review && !item.has_preliminary) flagType.push('Clean');

      let details = '';
      if (item.needs_review && item.claim_notes) {
        details += `Claim Note: ${item.claim_notes} | `;
      }
      if (item.has_preliminary && item.cpt_issues && item.cpt_issues.length > 0) {
        details += `CPT Issues: ${item.cpt_issues.map(i => `${i.cpt_code} - ${i.issue_category}`).join(', ')}`;
      }
      if (!item.needs_review && !item.has_preliminary) {
        details = 'No issues detected.';
      }

      return [
        escapeCSV(createDate),
        escapeCSV(dos),
        escapeCSV(caseId),
        escapeCSV(caseLink),
        escapeCSV(tech),
        escapeCSV(surg),
        escapeCSV(proc),
        escapeCSV(payer),
        escapeCSV(carrier),
        escapeCSV(org),
        escapeCSV(painCase),
        escapeCSV(approvalScore),
        escapeCSV(needsReviewScore),
        escapeCSV(approvalScoreV2),
        escapeCSV(dxReviewRequiredScore),
        escapeCSV(flagType.join(' & ')),
        escapeCSV(details)
      ].join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `USN_Coding_Quality_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">USN Coding Quality Dashboard</Heading>
        </Flex>

        {/* Filters */}
        <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6} borderWidth="1px">
          <form onSubmit={handleFilterSubmit}>
            <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
                <Input 
                  type="date" 
                  name="start_date" 
                  value={filters.start_date} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
                <Input 
                  type="date" 
                  name="end_date" 
                  value={filters.end_date} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Technologist</Text>
                <Input 
                  type="text" 
                  name="technologist" 
                  placeholder="Filter by tech..."
                  value={filters.technologist} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Surgeon</Text>
                <Input 
                  type="text" 
                  name="surgeon" 
                  placeholder="Filter by surgeon..."
                  value={filters.surgeon} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Organization</Text>
                <Select
                  name="technical_report_org"
                  value={filters.technical_report_org}
                  onChange={handleFilterChange}
                  size="sm"
                  placeholder="All Organizations"
                >
                  {availableOrgs.map((org, idx) => (
                    <option key={idx} value={org}>{org}</option>
                  ))}
                </Select>
              </Box>
              <HStack>
                <Button type="submit" colorScheme="blue" size="sm">Apply Filters</Button>
                <Button onClick={handleClearFilters} variant="outline" size="sm">Clear</Button>
                <Button 
                  onClick={exportToCSV} 
                  colorScheme="green" 
                  size="sm" 
                  isDisabled={processedData.allCases.length === 0}
                >
                  Export CSV
                </Button>
              </HStack>
            </Flex>
          </form>
        </Box>

        {error && (
          <Box bg="red.100" color="red.700" p={3} borderRadius="md" mb={4}>
            {error}
          </Box>
        )}

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            {/* KPIs (Always visible) */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">Total Cases Coded</StatLabel>
                  <StatNumber fontSize="2xl">{processedData.kpis.total}</StatNumber>
                </Stat>
              </Box>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">% Codes Passed Automated Review</StatLabel>
                  <StatNumber fontSize="2xl" color={processedData.kpis.passedCodePct < 100 ? "yellow.500" : "green.500"}>
                    {processedData.kpis.passedCodePct}%
                  </StatNumber>
                </Stat>
              </Box>
            </SimpleGrid>

            <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Volume Analysis</Tab>
                <Tab>Technologist Summary</Tab>
                <Tab>
                  Empty Cases Alert
                  {emptyCasesData.total_empty > 0 && (
                    <Badge ml={2} colorScheme="red" borderRadius="full">
                      {emptyCasesData.total_empty}
                    </Badge>
                  )}
                </Tab>
                <Tab fontWeight="semibold" color="blue.700">
                  Biller ICD-10 Compliance
                </Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Overview */}
                <TabPanel px={0} py={4}>
                  {/* Charts */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Daily Trend</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="Clean" stackId="a" fill="#48BB78" />
                            <Bar dataKey="Preliminary" stackId="a" fill="#ECC94B" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Top CPT Issues (Row-Level)</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.topIssues} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" tick={{fontSize: 11}} width={90} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#4299E1" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </SimpleGrid>

                  {/* All Cases Table */}
                  <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                    <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200">
                      <Heading size="sm" color="gray.800">All Cases ({processedData.allCases.length})</Heading>
                    </Box>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <SortableHeader label="Create Date" columnKey="created_at" />
                            <SortableHeader label="DOS" columnKey="date_of_surgery" />
                            <SortableHeader label="Case ID" columnKey="external_case_id" />
                            <SortableHeader label="Provider Info" columnKey="provider_info" />
                            <Th>Procedure</Th>
                            <Th>Org</Th>
                            <Th>Pain Case</Th>
                            <SortableHeader label="Payer/Carrier" columnKey="payer_carrier" />
                            <SortableHeader label="Approval Score" columnKey="approval_score" isNumeric />
                            <SortableHeader label="Needs Review Score" columnKey="needs_review_score" isNumeric />
                            <SortableHeader label="V2 Approval Score" columnKey="approval_score_v2" isNumeric />
                            <SortableHeader label="Dx Review Score" columnKey="dx_review_required_score" isNumeric />
                            <SortableHeader label="Status" columnKey="status" />
                            <Th>Details</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {processedData.allCases.length === 0 ? (
                            <Tr>
                              <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                                No cases found for the selected filters.
                              </Td>
                            </Tr>
                          ) : (
                            processedData.allCases.map((item, idx) => {
                              const approvalScore = item.approval_score_v2 !== null ? item.approval_score_v2 : item.approval_score;
                              const isManualReview = (
                                item.needs_review_score > 85 ||
                                (item.needs_review_score > 60 && approvalScore < 30) ||
                                (item.dx_review_required_score > 60 && approvalScore < 30)
                              );
                              
                              return (
                              <Tr key={idx} bg={isManualReview ? "red.50" : "transparent"} _hover={{ bg: isManualReview ? "red.100" : "gray.50" }}>
                                <Td whiteSpace="nowrap" color="gray.600">
                                  {item.created_at ? item.created_at.split('T')[0] : 'N/A'}
                                </Td>
                                <Td whiteSpace="nowrap" color="gray.600">
                                  {item.date_of_surgery || 'N/A'}
                                </Td>
                                <Td whiteSpace="nowrap">
                                  <Text 
                                    as="a" 
                                    href={`/ionm-strict-analysis/${item.external_case_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    color="blue.500"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                  >
                                    {item.external_case_id.substring(0,8)+'...'}
                                  </Text>
                                </Td>
                                <Td color="gray.600">
                                  <Box><Text as="span" fontWeight="medium">Tech:</Text> {item.technologist_name || 'N/A'}</Box>
                                  <Box><Text as="span" fontWeight="medium">Surg:</Text> {item.referring_provider || 'N/A'}</Box>
                                </Td>
                                <Td color="gray.600">
                                  <Text fontSize="sm">{item.procedure_name || 'N/A'}</Text>
                                </Td>
                                <Td color="gray.600">
                                  <Text fontSize="sm">{item.technical_report_org || 'N/A'}</Text>
                                </Td>
                                <Td color="gray.600">
                                  <Text fontSize="sm">{item.is_pain_management ? 'Yes' : 'No'}</Text>
                                </Td>
                                <Td color="gray.600">
                                  <Box><Text as="span" fontWeight="medium">Payer:</Text> {item.payer_type || 'N/A'}</Box>
                                  <Box><Text as="span" fontWeight="medium">Carrier:</Text> {item.carrier_name || 'N/A'}</Box>
                                </Td>
                                <Td isNumeric>
                                  {item.approval_score !== null && item.approval_score !== undefined ? (
                                    <Badge 
                                      colorScheme={item.approval_score >= 80 ? 'green' : item.approval_score >= 60 ? 'yellow' : 'red'}
                                      fontSize="sm"
                                    >
                                      {item.approval_score}
                                    </Badge>
                                  ) : (
                                    <Text color="gray.400" fontSize="sm">N/A</Text>
                                  )}
                                </Td>
                                <Td isNumeric>
                                  {item.needs_review_score !== null && item.needs_review_score !== undefined ? (
                                    <Badge 
                                      colorScheme={item.needs_review_score >= 80 ? 'red' : item.needs_review_score >= 40 ? 'yellow' : 'green'}
                                      fontSize="sm"
                                    >
                                      {item.needs_review_score}
                                    </Badge>
                                  ) : (
                                    <Text color="gray.400" fontSize="sm">N/A</Text>
                                  )}
                                </Td>
                                <Td isNumeric>
                                  {item.approval_score_v2 !== null && item.approval_score_v2 !== undefined ? (
                                    <Badge 
                                      colorScheme={item.approval_score_v2 >= 80 ? 'green' : item.approval_score_v2 >= 60 ? 'yellow' : 'red'}
                                      fontSize="sm"
                                    >
                                      {item.approval_score_v2}
                                    </Badge>
                                  ) : (
                                    <Text color="gray.400" fontSize="sm">N/A</Text>
                                  )}
                                </Td>
                                <Td isNumeric>
                                  {item.dx_review_required_score !== null && item.dx_review_required_score !== undefined ? (
                                    <Badge 
                                      colorScheme={item.dx_review_required_score >= 80 ? 'red' : item.dx_review_required_score >= 40 ? 'yellow' : 'green'}
                                      fontSize="sm"
                                    >
                                      {item.dx_review_required_score}
                                    </Badge>
                                  ) : (
                                    <Text color="gray.400" fontSize="sm">N/A</Text>
                                  )}
                                </Td>
                                <Td whiteSpace="nowrap">
                                  <Flex direction="column" gap={1} align="flex-start">
                                    {item.needs_review && (
                                      <Badge colorScheme="red">Claim Review</Badge>
                                    )}
                                    {item.has_preliminary && (
                                      <Badge colorScheme="yellow">Row Preliminary</Badge>
                                    )}
                                    {!item.needs_review && !item.has_preliminary && (
                                      <Badge colorScheme="green">Clean</Badge>
                                    )}
                                  </Flex>
                                </Td>
                                <Td maxW="400px" whiteSpace="normal" color="gray.600">
                                  {item.needs_review && item.claim_notes && (
                                    <Box mb={2}>
                                      <Text as="strong" color="red.600" fontSize="xs">Claim Note:</Text>
                                      <Text fontSize="xs" noOfLines={3} title={item.claim_notes}>{item.claim_notes}</Text>
                                    </Box>
                                  )}
                                  {item.has_preliminary && item.cpt_issues && item.cpt_issues.length > 0 && (
                                    <Box>
                                      <Text as="strong" color="yellow.600" fontSize="xs">CPT Issues:</Text>
                                      <UnorderedList pl={4} fontSize="xs" m={0}>
                                        {item.cpt_issues.map((issue, i) => (
                                          <ListItem key={i}>{issue.cpt_code} - {issue.issue_category}</ListItem>
                                        ))}
                                      </UnorderedList>
                                    </Box>
                                  )}
                                  {!item.needs_review && !item.has_preliminary && (
                                    <Text fontSize="xs" color="gray.500">No issues detected.</Text>
                                  )}
                                </Td>
                              </Tr>
                              );
                            })
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </TabPanel>

                {/* Tab 2: Volume Analysis */}
                <TabPanel px={0} py={4}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={2} color="gray.600">Processed Cases by Procedure Date</Heading>
                      <Text fontSize="xs" color="gray.500" mb={4}>Cases that have successfully had their technician report extracted.</Text>
                      <Box height="350px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedVolumeData.processedTrend} margin={{ top: 20, right: 30, left: 0, bottom: 25 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 11}} angle={-45} textAnchor="end" />
                            <YAxis tick={{fontSize: 12}} allowDecimals={false} />
                            <Tooltip />
                            <Legend verticalAlign="top" height={36}/>
                            {processedVolumeData.orgs.map((org, idx) => {
                              const colors = ['#3182CE', '#48BB78', '#ED8936', '#9F7AEA', '#F56565'];
                              return <Bar key={org} dataKey={org} stackId="a" fill={colors[idx % colors.length]} />;
                            })}
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={2} color="gray.600">Unprocessed Cases by Creation Date</Heading>
                      <Text fontSize="xs" color="gray.500" mb={4}>Cases missing extraction data (pending or failed). Grouped by the date they were created in the system.</Text>
                      <Box height="350px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedVolumeData.unprocessedTrend} margin={{ top: 20, right: 30, left: 0, bottom: 25 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 11}} angle={-45} textAnchor="end" />
                            <YAxis tick={{fontSize: 12}} allowDecimals={false} />
                            <Tooltip />
                            <Legend verticalAlign="top" height={36}/>
                            <Bar dataKey="Unprocessed" fill="#E53E3E" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </SimpleGrid>

                  {/* Volume Data Table */}
                  <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                    <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200">
                      <Heading size="sm" color="gray.800">Processed Volume Details</Heading>
                    </Box>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Procedure Date</Th>
                            <Th isNumeric>Total Cases</Th>
                            {processedVolumeData.orgs.map(org => (
                              <Th key={org} isNumeric>{org}</Th>
                            ))}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {processedVolumeData.processedTrend.length === 0 ? (
                            <Tr>
                              <Td colSpan={processedVolumeData.orgs.length + 2} textAlign="center" py={8} color="gray.500">
                                No data available.
                              </Td>
                            </Tr>
                          ) : (
                            // Reverse the array to show newest dates first in the table
                            [...processedVolumeData.processedTrend].reverse().map((day, idx) => (
                              <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                <Td whiteSpace="nowrap" fontWeight="medium">{day.date}</Td>
                                <Td isNumeric fontWeight="bold">{day.Total}</Td>
                                {processedVolumeData.orgs.map(org => (
                                  <Td key={org} isNumeric color={day[org] > 0 ? "gray.800" : "gray.400"}>
                                    {day[org]}
                                  </Td>
                                ))}
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </TabPanel>

                {/* Tab 3: Technologist Summary */}
                <TabPanel px={0} py={4}>
                  <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Technologist</Th>
                          <Th isNumeric>Total Cases</Th>
                          <Th isNumeric>Codes Passed</Th>
                          <Th isNumeric>% Codes Passed</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {processedData.techSummary.length === 0 ? (
                          <Tr>
                            <Td colSpan={4} textAlign="center" py={4}>No data available.</Td>
                          </Tr>
                        ) : (
                          processedData.techSummary.map((tech, index) => (
                            <Tr key={index} _hover={{ bg: "gray.50" }}>
                              <Td fontWeight="medium">
                                <Text 
                                  as="button" 
                                  color="blue.500" 
                                  _hover={{ textDecoration: 'underline' }} 
                                  onClick={() => handleTechnologistClick(tech.name)}
                                  textAlign="left"
                                >
                                  {tech.name}
                                </Text>
                              </Td>
                              <Td isNumeric>{tech.total}</Td>
                              <Td isNumeric>{tech.passedCodes}</Td>
                              <Td>
                                <Flex align="center" justify="flex-end" gap={3}>
                                  <Text color={tech.passedCodePct < 100 ? "yellow.600" : "green.600"} fontWeight={tech.passedCodePct < 100 ? "bold" : "normal"} minW="45px" textAlign="right">
                                    {tech.passedCodePct}%
                                  </Text>
                                  <Progress 
                                    value={Number(tech.passedCodePct)} 
                                    colorScheme={tech.passedCodePct < 100 ? "yellow" : "green"} 
                                    size="sm" 
                                    width="100px" 
                                    borderRadius="md" 
                                    bg="gray.100"
                                  />
                                </Flex>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* Tab 4: Empty Cases Alert */}
                <TabPanel px={0} py={4}>
                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                    <Heading size="sm" mb={4} color="gray.600">Integration Volume & Empty Cases (Last 30 Days)</Heading>
                    <Text fontSize="sm" color="gray.500" mb={4}>
                      Tracks total cases created via API, successfully coded cases, and empty cases (0 files). This chart always shows the last 30 days, ignoring the global date filters.
                    </Text>
                    <Box height="300px">
                      {emptyCasesData.daily_trend && emptyCasesData.daily_trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={emptyCasesData.daily_trend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="total_cases" name="Total Cases" fill="#3182CE" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="coded_cases" name="Coded Cases" fill="#48BB78" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="empty_cases" name="Empty Cases (Errors)" fill="#E53E3E" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Flex height="100%" align="center" justify="center">
                          <Text color="gray.500">No cases found in the last 30 days.</Text>
                        </Flex>
                      )}
                    </Box>
                  </Box>

                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" overflowX="auto">
                      <Heading size="sm" mb={4} color="gray.600">Empty Cases List</Heading>
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>External Case ID</Th>
                            <Th>Date Created</Th>
                            <Th>Creator Email</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {emptyCasesData.cases && emptyCasesData.cases.length > 0 ? (
                            emptyCasesData.cases.map((c, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium">{c.external_case_id}</Td>
                                <Td>{new Date(c.date_created).toLocaleString()}</Td>
                                <Td>{c.creator_email}</Td>
                              </Tr>
                            ))
                          ) : (
                            <Tr>
                              <Td colSpan={3} textAlign="center" py={4} color="gray.500">
                                No empty cases found.
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" overflowX="auto">
                      <Heading size="sm" mb={4} color="gray.600">Coded Cases & Delay</Heading>
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>External Case ID</Th>
                            <Th>Date Created</Th>
                            <Th>Procedure Date</Th>
                            <Th isNumeric>Days Delayed</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {emptyCasesData.coded_cases && emptyCasesData.coded_cases.length > 0 ? (
                            emptyCasesData.coded_cases.map((c, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium">
                                  <Text 
                                    as="a" 
                                    href={`/ionm-strict-analysis/${c.external_case_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    color="blue.500"
                                    _hover={{ textDecoration: 'underline' }}
                                  >
                                    {c.external_case_id}
                                  </Text>
                                </Td>
                                <Td>{new Date(c.date_created).toLocaleDateString()}</Td>
                                <Td>{c.procedure_date || 'N/A'}</Td>
                                <Td isNumeric>
                                  {c.days_delayed !== null ? (
                                    <Badge colorScheme={c.days_delayed > 28 ? 'red' : 'green'}>
                                      {c.days_delayed} days
                                    </Badge>
                                  ) : (
                                    'N/A'
                                  )}
                                </Td>
                              </Tr>
                            ))
                          ) : (
                            <Tr>
                              <Td colSpan={4} textAlign="center" py={4} color="gray.500">
                                No coded cases found.
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </SimpleGrid>
                </TabPanel>

                {/* Tab 5: Biller ICD-10 Compliance (Revedy Coded vs. Biller Submitted) */}
                <TabPanel px={0} py={4}>
                  <USNBillerComplianceTab accessToken={accessToken} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>
    </Box>
  );
};

export default USNCodingQualityDashboard;
